import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import souvenirService, { SouvenirOrder } from '../../services/souvenir.service'
import LoadingSpinner from '../../components/LoadingSpinner'
import { EP_STATUS_LABELS } from '../../constants/easyparcel-status'
import {
  ArrowLeftIcon,
  TruckIcon,
  DocumentArrowDownIcon,
  MapPinIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-indigo-100 text-indigo-800',
  READY: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-cyan-100 text-cyan-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
}

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const [order, setOrder] = useState<SouvenirOrder | null>(null)
  const [loading, setLoading] = useState(true)
  const [tracking, setTracking] = useState(false)

  const loadOrder = useCallback(async () => {
    if (!id) return
    try {
      const res = await souvenirService.getOrder(id)
      setOrder(Array.isArray(res.data) ? res.data[0] : res.data?.data || res.data)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load order')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadOrder()
  }, [loadOrder])

  const handleTrack = async () => {
    if (!order) return
    setTracking(true)
    try {
      const res = await souvenirService.trackOrder(order.id)
      const updated = res.data?.data?.order
      if (updated) setOrder(updated)
      toast.success(
        res.data?.data?.epStatusLabel
          ? `${t('orders.trackingUpdated')}: ${res.data.data.epStatusLabel}`
          : t('orders.trackingUpdated'),
      )
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('orders.trackingFailed'))
    } finally {
      setTracking(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <h2 className="text-xl font-semibold text-gray-900">Order not found</h2>
      </div>
    )
  }

  const subtotal = Number(order.subtotal)
  const shipping = Number(order.shippingCost)
  const fee = Number(order.platformFee)
  const total = Number(order.totalAmount)
  const addr = order.deliveryAddress

  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/my-orders"
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        {t('orders.backToOrders')}
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{order.orderNumber}</h1>
          <p className="text-sm text-gray-500">
            {new Date(order.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {order.epStatus && order.status === 'SHIPPED' && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              <TruckIcon className="h-3.5 w-3.5 inline mr-1" />
              {EP_STATUS_LABELS[order.epStatus] || `EP ${order.epStatus}`}
            </span>
          )}
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {t(`orders.status.${order.status.toLowerCase()}`, order.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.items')}</h3>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex items-center gap-4">
                  {item.images?.[0] ? (
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="h-16 w-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-200 rounded-lg" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {item.quantity} × RM{Number(item.unitPrice).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-medium text-gray-900">
                    RM{Number(item.subtotal).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Tracking */}
          {order.deliveryMethod === 'DELIVERY' && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('orders.tracking')}
                </h3>
                {order.awbNumber && (
                  <button
                    onClick={handleTrack}
                    disabled={tracking}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${tracking ? 'animate-spin' : ''}`} />
                    {t('orders.track')}
                  </button>
                )}
              </div>

              {order.awbNumber ? (
                <>
                  <div className="flex items-center gap-4 flex-wrap text-sm">
                    <span className="text-gray-500">
                      {order.courierName} · {order.serviceName}
                    </span>
                    <span className="font-mono text-gray-700">{order.awbNumber}</span>
                    {order.awbUrl && (
                      <a
                        href={order.awbUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        {t('orders.viewLabel')}
                      </a>
                    )}
                    {order.trackingUrl && (
                      <a
                        href={order.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {t('orders.courierSite')}
                      </a>
                    )}
                  </div>

                  {/* Timeline */}
                  {order.epStatusHistory?.length ? (
                    <ol className="mt-5 border-l-2 border-gray-200 ml-3 space-y-4">
                      {order.epStatusHistory.map((h, idx) => (
                        <li key={idx} className="ml-4">
                          <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full bg-primary-500" />
                          <p className="text-sm font-medium text-gray-900">
                            {h.label ||
                              EP_STATUS_LABELS[String(h.status ?? '')] ||
                              h.description ||
                              `Status ${h.status}`}
                          </p>
                          {(h.at || h.date || h.timestamp) && (
                            <p className="text-xs text-gray-400">
                              {new Date(h.at || h.date || h.timestamp!).toLocaleString()}
                            </p>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-gray-400">{t('orders.noTrackingYet')}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400">{t('orders.noTrackingYet')}</p>
              )}
            </div>
          )}

          {/* Address */}
          {order.deliveryMethod === 'DELIVERY' && addr && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                {t('checkout.deliveryAddress')}
              </h3>
              <div className="flex gap-3 text-sm text-gray-600">
                <MapPinIcon className="h-5 w-5 text-gray-400 shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">{addr.name} · {addr.phone}</p>
                  <p>{addr.address}, {addr.city}, {addr.state} {addr.postalCode}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white shadow rounded-lg p-6 h-fit">
          <h3 className="text-lg font-medium text-gray-900">{t('cart.summary')}</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('cart.subtotal')}</span>
              <span className="font-medium">RM{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('checkout.shippingFee')}</span>
              <span className="font-medium">
                {order.deliveryMethod === 'PICKUP' ? '—' : `RM${shipping.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('checkout.platformFee')}</span>
              <span className="font-medium">RM{fee.toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-base">
              <span className="font-semibold text-gray-900">{t('checkout.total')}</span>
              <span className="font-bold text-gray-900">RM{total.toFixed(2)}</span>
            </div>
          </div>
          {order.paymentStatus === 'PAID' && (
            <p className="mt-4 text-xs text-green-600 font-medium text-center">
              {t('orders.paid')} ✓
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDetailPage
